/* =====================================================================
   sn-cart.js — tiroir panier et page panier du thème SN
   JavaScript sans dépendance, chargé en defer après pubsub.js et global.js.

   Le contenu du panier reste calculé en Liquid et rechargé par les scripts
   Dawn (cart-drawer.js, cart.js) : ce fichier ne fait qu'ajouter
   1. <sn-cart-progress> : anime la barre de paliers depuis sa largeur précédente ;
   2. <sn-cart-sync>     : recopie le nombre d'articles dans l'en-tête du tiroir
                           quand Dawn ne recharge que la liste ;
   3. <sn-cart-upsell>   : carrousel à flèches, choix de variante et ajout au
                           panier en AJAX (puis rechargement Dawn du tiroir) ;
   4. page /cart         : garde le tiroir et la page d'accord entre eux.
   ===================================================================== */
(() => {
  if (window.SnCart) return; // le fichier peut être inclus deux fois (tiroir + page panier)

  const SnCart = (window.SnCart = { progress: {} });
  const DURATION = 450; // ms, comme la transition CSS de la barre
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasPubSub = () => typeof subscribe === 'function' && typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined';

  /* ---------------------------------------------------------------
     1. Barre de paliers : le HTML arrive avec la bonne largeur ; on
     repart de la largeur affichée juste avant pour garder l'animation.
     --------------------------------------------------------------- */
  if (!customElements.get('sn-cart-progress')) {
    customElements.define(
      'sn-cart-progress',
      class SnCartProgress extends HTMLElement {
        connectedCallback() {
          const ctx = this.dataset.context || 'drawer';
          const target = parseFloat(this.dataset.pct) || 0;
          const fill = this.querySelector('.sn-progress__fill');
          const last = SnCart.progress[ctx];
          const now = Date.now();

          if (!fill || !last || reducedMotion()) {
            SnCart.progress[ctx] = { from: target, to: target, start: 0 };
            return;
          }

          let from = last.to;
          let elapsed = 0;
          if (last.to === target) {
            // Même valeur : Dawn recharge parfois la zone deux fois de suite ;
            // on reprend l'animation en cours au lieu de sauter à la fin.
            elapsed = now - last.start;
            if (elapsed >= DURATION) return;
            from = last.from;
          } else {
            SnCart.progress[ctx] = { from, to: target, start: now };
          }
          if (from === target) return;

          fill.style.transition = 'none';
          fill.style.width = from + '%';
          fill.getBoundingClientRect(); // force le calcul avant de relancer la transition
          fill.style.transition = '';
          fill.style.transitionDelay = elapsed ? `-${elapsed}ms` : '';
          fill.style.width = target + '%';
        }
      }
    );
  }

  /* ---------------------------------------------------------------
     2. Nombre d'articles dans l'en-tête du tiroir
     --------------------------------------------------------------- */
  if (!customElements.get('sn-cart-sync')) {
    customElements.define(
      'sn-cart-sync',
      class SnCartSync extends HTMLElement {
        connectedCallback() {
          const drawer = this.closest('cart-drawer');
          if (!drawer) return;
          const count = parseInt(this.dataset.count, 10) || 0;
          drawer.querySelectorAll('[data-sn-cart-count]').forEach((el) => {
            el.textContent = count;
            el.hidden = count === 0;
          });
        }
      }
    );
  }

  /* ---------------------------------------------------------------
     3. Recommandations « Finalisez votre commande »
     --------------------------------------------------------------- */
  if (!customElements.get('sn-cart-upsell')) {
    customElements.define(
      'sn-cart-upsell',
      class SnCartUpsell extends HTMLElement {
        constructor() {
          super();
          // Le rail est dans <cart-drawer-items> : sans cet arrêt, Dawn prendrait le
          // changement d'une liste de variantes pour un changement de quantité.
          this.addEventListener('change', (event) => {
            event.stopPropagation();
            const card = event.target.closest('[data-sn-upsell-card]');
            if (card && event.target.matches('select')) this.onVariantChange(card);
          });

          this.addEventListener('click', (event) => {
            const addButton = event.target.closest('[data-sn-upsell-add]');
            if (addButton) {
              event.preventDefault();
              this.add(addButton.closest('[data-sn-upsell-card]'), addButton);
              return;
            }
            if (event.target.closest('[data-sn-upsell-prev]')) this.scrollByCard(-1);
            if (event.target.closest('[data-sn-upsell-next]')) this.scrollByCard(1);
          });

          this.onScroll = this.updateArrows.bind(this);
        }

        connectedCallback() {
          this.track = this.querySelector('[data-sn-upsell-track]');
          this.prevButton = this.querySelector('[data-sn-upsell-prev]');
          this.nextButton = this.querySelector('[data-sn-upsell-next]');
          if (!this.track || !this.prevButton || !this.nextButton) return;

          this.track.addEventListener('scroll', this.onScroll, { passive: true });
          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(this.onScroll);
            this.resizeObserver.observe(this.track);
          }
          this.updateArrows();
        }

        disconnectedCallback() {
          if (this.track) this.track.removeEventListener('scroll', this.onScroll);
          if (this.resizeObserver) this.resizeObserver.disconnect();
        }

        updateArrows() {
          if (!this.track || !this.prevButton) return;
          const max = this.track.scrollWidth - this.track.clientWidth;
          const arrows = this.prevButton.parentElement;
          if (arrows) arrows.hidden = max <= 2;
          this.prevButton.disabled = this.track.scrollLeft <= 2;
          this.nextButton.disabled = this.track.scrollLeft >= max - 2;
        }

        scrollByCard(direction) {
          if (!this.track) return;
          const card = this.track.querySelector('.sn-upsell__card');
          if (!card) return;
          const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
          this.track.scrollBy({
            left: direction * (card.getBoundingClientRect().width + gap),
            behavior: reducedMotion() ? 'auto' : 'smooth',
          });
        }

        variantsOf(card) {
          if (!card.snVariants) {
            const script = card.querySelector('[data-sn-upsell-variants]');
            try {
              card.snVariants = script ? JSON.parse(script.textContent) : [];
            } catch (e) {
              card.snVariants = [];
            }
          }
          return card.snVariants;
        }

        onVariantChange(card) {
          const selected = Array.from(card.querySelectorAll('select[data-sn-option-index]'))
            .sort((a, b) => Number(a.dataset.snOptionIndex) - Number(b.dataset.snOptionIndex))
            .map((select) => select.value);
          const variant = this.variantsOf(card).find((v) => v.options.every((value, i) => value === selected[i]));
          const button = card.querySelector('[data-sn-upsell-add]');
          const label = button && button.querySelector('.sn-upsell__btn-label');
          const idInput = card.querySelector('[data-sn-upsell-id]');
          const price = card.querySelector('[data-sn-upsell-price]');

          this.setError(card, '');
          if (!button || !label || !idInput) return;

          if (!variant) {
            button.disabled = true;
            label.textContent = this.dataset.soldoutLabel || '';
            return;
          }

          idInput.value = variant.id;
          button.disabled = !variant.available;
          label.textContent = variant.available ? this.dataset.addLabel || '' : this.dataset.soldoutLabel || '';
          if (price) {
            // Montants mis en forme par Shopify (filtre money) dans le JSON de la carte
            price.innerHTML =
              (variant.compare ? `<s class="sn-upsell__price-was">${variant.compare}</s>` : '') +
              `<span class="sn-upsell__price-now">${variant.price}</span>`;
          }
        }

        setError(card, message) {
          const error = card && card.querySelector('.sn-upsell__error');
          if (!error) return;
          error.textContent = message;
          error.hidden = !message;
        }

        setLoading(button, loading) {
          button.disabled = loading;
          button.classList.toggle('is-loading', loading);
          button.setAttribute('aria-busy', loading ? 'true' : 'false');
          const spinner = button.querySelector('.loading__spinner');
          if (spinner) spinner.classList.toggle('hidden', !loading);
        }

        async add(card, button) {
          if (!card || !button || button.disabled) return;
          const idInput = card.querySelector('[data-sn-upsell-id]');
          const variantId = Number(idInput && idInput.value);
          if (!variantId) return;

          const routes = window.routes || {};
          const drawer = document.querySelector('cart-drawer');
          const sections =
            drawer && typeof drawer.getSectionsToRender === 'function'
              ? drawer.getSectionsToRender().map((section) => section.id)
              : [];

          this.setError(card, '');
          this.setLoading(button, true);

          try {
            const response = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify({
                items: [{ id: variantId, quantity: 1 }],
                sections,
                sections_url: window.location.pathname,
              }),
            });
            const data = await response.json();
            if (!response.ok || data.status) {
              throw new Error(data.description || data.message || (window.cartStrings && window.cartStrings.error) || '');
            }

            if (hasPubSub()) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sn-cart-upsell', productVariantId: variantId, cartData: data });
            }

            if (drawer && data.sections && typeof drawer.renderContents === 'function') {
              drawer.classList.remove('is-empty');
              drawer.renderContents(data);
              // Le bouton cliqué a été remplacé : on remet le focus dans le tiroir
              setTimeout(() => {
                const inner = drawer.querySelector('.drawer__inner');
                if (inner) inner.focus({ preventScroll: true });
              }, 50);
            } else {
              window.location.href = routes.cart_url || '/cart';
            }
          } catch (error) {
            this.setLoading(button, false);
            this.setError(card, error.message || (window.cartStrings && window.cartStrings.error) || '');
          }
        }
      }
    );
  }

  /* ---------------------------------------------------------------
     4. Page panier : Dawn ne met pas à jour le tiroir quand on change
     une quantité sur /cart (et inversement). On recharge la partie
     qui n'a pas bougé, par la Section Rendering API.
     --------------------------------------------------------------- */
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

  function refreshDrawer() {
    const drawer = document.querySelector('cart-drawer');
    const current = document.getElementById('CartDrawer');
    if (!drawer || !current || !window.routes) return null;
    return fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((html) => {
        const doc = parse(html);
        const fresh = doc.getElementById('CartDrawer');
        if (!fresh) return;
        current.innerHTML = fresh.innerHTML;
        const freshDrawer = doc.querySelector('cart-drawer');
        drawer.classList.toggle('is-empty', !!(freshDrawer && freshDrawer.classList.contains('is-empty')));
        const overlay = current.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', () => drawer.close());
      })
      .catch((error) => console.error(error));
  }

  function refreshCartPage() {
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    if (!items || !window.routes) return null;
    const ids = [items.dataset.id, footer && footer.dataset.id].filter(Boolean);
    return fetch(`${window.routes.cart_url}?sections=${ids.join(',')}`)
      .then((response) => response.json())
      .then((sections) => {
        let empty = false;
        [items, footer].forEach((wrapper) => {
          if (!wrapper || !sections[wrapper.dataset.id]) return;
          const doc = parse(sections[wrapper.dataset.id]);
          const fresh = doc.querySelector('.js-contents');
          const target = wrapper.querySelector('.js-contents');
          if (fresh && target) target.innerHTML = fresh.innerHTML;
          if (wrapper === items) empty = !!doc.querySelector('cart-items.is-empty');
        });
        const cartItems = document.querySelector('cart-items');
        if (cartItems) cartItems.classList.toggle('is-empty', empty);
        if (footer) footer.classList.toggle('is-empty', empty);
      })
      .catch((error) => console.error(error));
  }

  if (hasPubSub()) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || !document.getElementById('main-cart-items')) return; // seulement sur /cart
      const drawer = document.querySelector('cart-drawer');
      const drawerOpen = !!(drawer && drawer.classList.contains('active'));

      if (event.source === 'cart-items') {
        // Modification faite dans le tiroir ouvert : on met la page à jour ; sinon l'inverse
        return drawerOpen ? refreshCartPage() : refreshDrawer();
      }
      if (event.source === 'sn-cart-upsell') return refreshCartPage();
    });
  }
})();
